import { Directive, ElementRef, OnDestroy, AfterViewInit } from '@angular/core';
import { MatHorizontalStepper } from '@angular/material/stepper';
import { Subscription } from 'rxjs';

/**
 * Mantém a altura do `.mat-horizontal-content-container` em dia com o step
 * ativo do `mat-horizontal-stepper`.
 *
 * ── Por que existe (Safari + Material 11) ──────────────────────────────────
 * O container tem `overflow: hidden` e cada step entra via
 * `transform: translateX(...)` do `@angular/animations`. No Chrome, o Angular
 * recomputa e commita a nova altura ANTES do rAF em que o transform começa a
 * pintar — o conteúdo desliza dentro da altura certa. No Safari o pipeline do
 * WebKit despacha o transform numa etapa que roda ANTES desse commit: o
 * conteúdo entra CLIPADO e a caixa "salta" para a altura correta só depois.
 *
 * Fix: no `selectionChange`, medir SÍNCRONO o `scrollHeight` do step que está
 * entrando e já commitar `container.style.height`. A leitura de
 * `container.offsetHeight` força o WebKit a flushar layout no ato, então
 * quando a rAF do transform roda a altura já vale.
 *
 * Um `ResizeObserver` acompanha o step ativo DENTRO da mesma etapa: sem ele o
 * container trava na altura medida na troca e qualquer linha nova (adicionar
 * um chair, um autor) nasce clipada.
 *
 * ── Procedência ───────────────────────────────────────────────────────────
 * Receita validada no assistente de submissão (`create-submission`, commit
 * afacdef64), onde o bug foi diagnosticado. Aqui ela virou directive para não
 * existir em duas cópias. O `create-submission` ainda tem a versão inline —
 * migrá-lo exige quem consiga testar em Safari, então ficou como pendência.
 */
@Directive({
  selector: 'mat-horizontal-stepper[appStepperAutoHeight]',
})
export class StepperAutoHeightDirective implements AfterViewInit, OnDestroy {
  private selectionSub?: Subscription;
  private contentResizeObs?: ResizeObserver;

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private stepper: MatHorizontalStepper,
  ) {}

  ngAfterViewInit(): void {
    this.selectionSub = this.stepper.selectionChange.subscribe(ev => {
      this.applyHeight(ev.selectedIndex);
      this.observeActiveStep(ev.selectedIndex);
    });
    // `selectionChange` só emite em TROCAS — o step inicial precisa ser
    // observado à parte, senão crescer nele não ajusta o container.
    this.observeActiveStep(this.stepper.selectedIndex ?? 0);
  }

  ngOnDestroy(): void {
    this.selectionSub?.unsubscribe();
    this.contentResizeObs?.disconnect();
  }

  private applyHeight(selectedIndex: number): void {
    // `Promise.resolve().then` roda depois do CD atual (que aplica o
    // aria-hidden nos steps) e ainda antes do rAF que pinta o transform.
    Promise.resolve().then(() => {
      const parts = this.resolveParts(selectedIndex);
      if (!parts) { return; }
      const h = parts.content.scrollHeight;
      if (h > 0) {
        parts.container.style.height = `${h}px`;
        // Leitura deliberada: força o flush de layout no WebKit.
        // tslint:disable-next-line: no-unused-expression
        parts.container.offsetHeight;
      }
    });
  }

  private observeActiveStep(selectedIndex: number): void {
    this.contentResizeObs?.disconnect();
    if (typeof ResizeObserver === 'undefined') { return; }
    Promise.resolve().then(() => {
      const parts = this.resolveParts(selectedIndex);
      if (!parts) { return; }
      this.contentResizeObs = new ResizeObserver(() => {
        const h = parts.content.scrollHeight;
        if (h > 0) { parts.container.style.height = `${h}px`; }
      });
      this.contentResizeObs.observe(parts.content);
    });
  }

  /** Container animado + conteúdo do step pedido, ou null se ainda não há DOM. */
  private resolveParts(selectedIndex: number): { container: HTMLElement, content: HTMLElement } | null {
    const host = this.hostRef.nativeElement;
    const container = host.querySelector('.mat-horizontal-content-container') as HTMLElement | null;
    const contents = host.querySelectorAll('.mat-horizontal-stepper-content');
    const content = contents[selectedIndex] as HTMLElement | undefined;
    return (container && content) ? { container, content } : null;
  }
}
